import React, {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {getPercent} from '@ey/utils';
import {StackedBarChart} from '@ey/canvascoreservices/Charts';
import theme, {lightColor, darkColor} from '@ey/styled-theme';

import Utility from '../../../util/utilityfunctions';
import {labels} from '../../../util/utils';
import normalizeDashboardFilters from '../../../util/dashboard/normalizeDashboardFilters';
import {
	EngagementDataViewType,
	AuditInsightViewType,
	projectSessionKeys,
	dashboardTabId,
	insightFilterId,
	insightTypes
} from '../../../util/uiconstants';

import {loadStorage, saveStorage} from '../../Common/localStorage';
import {clearFilterDashboardTiles, updateFilterDashboardTiles} from '../../../actions/Metrics/dashboardFiltersAction';

import OutputText from '@ey/canvascoreservices/OutputText';

import Tile from '../Tile';
import PlaceHolder from '../Placeholder/Placeholder';
import Select from '../../CoreWrappers/Select';
import Legend from '../Charts/Legend';

const AuditInsightTileLayout = (props) => {
	const dashboardContext = useSelector((store) => store.dashboardContext);
	const {auditData, config, type} = props;
	const [chartData1, setChartData1] = useState(null);
	const [chartData2, setChartData2] = useState(null);
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const [toggleOption, setToggleOptionSwitcher] = useState(
		config.defaultToggleOption ? config.defaultToggleOption : {id: 0}
	);
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.insightSession}`;

	const isDark = document.getElementsByTagName('body')[0].getAttribute('motif-theme') === 'dark' ? 'dark' : 'light';
	const themeColor = isDark === 'dark' ? darkColor : lightColor;

	const dispatch = useDispatch();
	const toggleOptionRef = useRef();
	toggleOptionRef.current = toggleOption ?? null;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		const currentKey = type === 'finding' ? 'auditFindingFilterid' : 'auditStrategyFilterid';
		if (currentState && currentState[currentKey]) {
			setToggleOptionSwitcher(currentState[currentKey]);
		} else {
			if (currentState) {
				currentState[currentKey] = config.defaultToggleOption;
			}
		}
	}, []);

	useEffect(() => {
		buildChartData(dashboardContext.engagements);
	}, [auditData, dashboardContext, props.dataLoading]);

	useEffect(() => {
		if (_.size(dashboardContext.engagements) > 0) {
			buildChartData(dashboardContext.engagements);
		}
	}, [props.viewType, toggleOption]);

	const getFilteredData = (allData, dataFilterAttribute, seriesType) => {
		let filteredData = allData.filter((data) => {
			return seriesType.typeFilterIds.includes(data[dataFilterAttribute]);
		});
		return filteredData;
	};

	const getSeriesData = (allData, dataFilterAttribute, seriesType) => {
		let seriesData = seriesType;
		let filteredData = allData;
		if (props.viewType !== AuditInsightViewType.all) {
			filteredData = getFilteredData(allData, dataFilterAttribute, seriesType);
		}

		let totalCount = 0;
		seriesData.series.forEach((item, index) => {
			if (item.calculationFormula) {
				const engs = item.calculationFormula(filteredData, item.dataAttributeName);
				item.itemCount = [...engs].length;
				item.engagements = [...engs].map(({engId}) => engId);
			} else {
				item.itemCount = _.sum(filteredData.map((data) => data[item.dataAttributeName]));
				item.engagements = filteredData.filter((eng) => eng[item.dataAttributeName] > 0).map(({engId}) => engId);
			}

			if (item.itemCount >= 0) {
				totalCount = totalCount + item.itemCount;
			} else {
				item.itemCount = 0;
			}

			if (item.dataAttributeAppend1) {
				item[item.dataAttributeAppend1 + 'Count'] = _.sum(filteredData.map((data) => data[item.dataAttributeAppend1]));
			}
			if (item.dataAttributeAppend2) {
				item[item.dataAttributeAppend2 + 'Count'] = _.sum(filteredData.map((data) => data[item.dataAttributeAppend2]));
			}
		});

		seriesData.totalCount = totalCount >= 0 ? totalCount : 0;

		return seriesData;
	};

	const primaryEngagementColors = [themeColor[`blue200`], themeColor[`blue400`], themeColor[`blue600`]];

	const getChartHeader = (engagementType, totalCount, chartTitleLabel, appendedLabel) => {
		let headerText = chartTitleLabel;
		headerText = `${headerText} ${appendedLabel}`;
		headerText = headerText.replace('{0}', Utility.getFormattedNumber(numberFormat, totalCount));

		return headerText;
	};

	const getSeriesColor = (index) => {
		if (index < primaryEngagementColors.length) {
			return primaryEngagementColors[index];
		}

		return primaryEngagementColors[0];
	};

	const barStyling1 = {
		styleName: 'auditInsightItem',
		chartWrapper: 'auditInsightchart'
	};

	const barStyling2 = {
		styleName: 'groupReportItem',
		chartWrapper: 'groupAuditchart'
	};

	const getPlaceholderFormat = (message) => {
		let element = Utility.createRichTextElement(message);
		let tooltipPlaceholder = element && element.props.dangerouslySetInnerHTML.__html.replace(/<[^>]+>/g, '');
		return (
			<PlaceHolder className="placeholder-message" placeholderMessage={''} tooltipPlaceholder={tooltipPlaceholder}>
				{element}
			</PlaceHolder>
		);
	};

	const updateChartSeries = (series, config) => {
		Utility.adjustPercentages(series);

		for (let counter = 0; counter < series.length; counter++) {
			let barItem = series[counter];
			const formattedCount = Utility.getFormattedNumber(numberFormat, barItem.count);
			let toolTip = `${barItem.label} | ${formattedCount} (${barItem.percentage}%)`;
			if (config.appendTooltip) {
				toolTip = config.appendTooltip(toolTip, barItem);
			}
			barItem.toolTip = toolTip;
		}

		return series;
	};

	const buildChartData = (engagements) => {
		if (props.dataLoading || _.isUndefined(props.dataLoading)) {
			return true;
		}

		// if(_.size(engagements) <=0 || _.isUndefined(auditData)){
		// 	return;
		// }
		let filterData;
		let engagementList = engagements || [];

		// filtering the context for dashboard context
		filterData = auditData.filter(
			(eng) => engagementList.some((item) => item === eng.engId) || _.isEmpty(engagementList)
		);

		// filterData = auditData;

		let dataType = config.chart1DataConfiguration;
		if (config.chart2DataConfiguration && toggleOption.id == config.chart2DataConfiguration.id) {
			dataType = config.chart2DataConfiguration;
		}
		if (config.chart3DataConfiguration && toggleOption.id == config.chart3DataConfiguration.id) {
			dataType = config.chart3DataConfiguration;
		}
		let chartTitleHeader =
			props.viewType === AuditInsightViewType.groupRole
				? dataType.primary.chartTitleLabel
				: dataType.allEngagementHeader;
		let primaryEngagementData = getSeriesData(filterData, dataType.dataFilterAttribute, dataType.primary);

		const regionalComponentEngagementData = getSeriesData(
			filterData,
			dataType.dataFilterAttribute,
			dataType.regionalandComponent
		);

		let barItems1 = [];
		primaryEngagementData.series.forEach((item, index) => {
			let percentage = getPercent(item.itemCount, primaryEngagementData.totalCount);
			let toolTip = `${item.label} | ${item.itemCount} (${percentage}%)`;
			let seriesItem = item;
			seriesItem = _.extend(seriesItem, {
				count: item.itemCount,
				label: item.label,
				statusClass: item.statusClass,
				color: item.color ? item.color : getSeriesColor(index),
				percentage: percentage,
				toolTip: toolTip
			});
			barItems1.push(seriesItem);
		});

		updateChartSeries(barItems1, dataType);

		const onColumClick = (e, viewType = 0) => {
			const handlerOnClearFilter = () => {
				dispatch(clearFilterDashboardTiles(dashboardTabId.insights));
			};

			const series = e.series.userOptions.label;
			let category = props.config.tileTitle;
			if (props.type === insightTypes.auditstrategy || props.type === insightTypes.finding)
				category = toggleOptionRef.current.label;
			if (props.type === insightTypes.digitalAudit) category = labels.digitalAuditLabel;

			const getFilteredEngagemets = () => {
				const viewTypes = ['primary', 'regionalandComponent'];
				let engagements = props.config.chart1DataConfiguration[viewTypes[viewType]].series?.find(
					({label}) => label === series
				)?.engagements;
				if (!engagements) {
					engagements = props.config.chart2DataConfiguration[viewTypes[viewType]].series?.find(
						({label}) => label === series
					)?.engagements;
				}
				if (!engagements) {
					engagements = props.config.chart3DataConfiguration[viewTypes[viewType]].series?.find(
						({label}) => label === series
					)?.engagements;
				}
				return engagements;
			};

			let type = insightFilterId.strategy,
				subtype = null;
			if (props.type === insightTypes.risk) type = insightFilterId.risks;
			if (props.type === insightTypes.finding) type = insightFilterId.findings;
			if (props.type === insightTypes.auditstrategy) subtype = toggleOptionRef.current.id;
			if (props.type === insightTypes.digitalAudit) subtype = -1;

			const filter = {
				serieLabel: series,
				categoryLabel: category,
				type,
				subtype,
				onClearChip: handlerOnClearFilter
			};

			const dashboardFilters = normalizeDashboardFilters([filter], getFilteredEngagemets(), handlerOnClearFilter);

			dispatch(updateFilterDashboardTiles(dashboardTabId.insights, dashboardFilters));
		};

		let appendedLabel = primaryEngagementData.totalCount === 1 ? dataType.totalCount1Label : dataType.totalCountLabel;
		const barChartData1 = {
			barStyling: dataType.primary.barStyling ? dataType.primary.barStyling : barStyling1,
			headerText: getChartHeader(
				EngagementDataViewType.primary,
				primaryEngagementData.totalCount,
				chartTitleHeader,
				appendedLabel
			),
			barMaxValue: primaryEngagementData.totalCount,
			showBarTitleTip: true,
			barItems: barItems1,
			showCustomLegend: _.isUndefined(dataType.showCustomLegend) ? true : dataType.showCustomLegend,
			totalCount: primaryEngagementData.totalCount,
			showChartSeries: primaryEngagementData.totalCount === 0 ? false : true,
			onColumClick: (e) => onColumClick(e, 0)
		};

		setChartData1(barChartData1);

		if (props.viewType === AuditInsightViewType.all) {
			// No need to render second chart
			setChartData2(null);
			return;
		}

		let barItems2 = [];
		regionalComponentEngagementData.series.forEach((item, index) => {
			let percentage = getPercent(item.itemCount, regionalComponentEngagementData.totalCount);
			item.percentage = percentage;
			let toolTip = `${item.label} | ${item.itemCount} (${percentage}%)`;
			let seriesItem = item;
			seriesItem = _.extend(seriesItem, {
				count: item.itemCount,
				label: item.label,
				statusClass: item.statusClass,
				color: item.color ? item.color : getSeriesColor(index),
				percentage: percentage,
				toolTip: toolTip
			});
			barItems2.push(seriesItem);
		});

		updateChartSeries(barItems2, dataType);

		chartTitleHeader =
			props.viewType === AuditInsightViewType.groupRole
				? dataType.regionalandComponent.chartTitleLabel
				: dataType.allEngagementHeader;
		appendedLabel =
			regionalComponentEngagementData.totalCount === 1 ? dataType.totalCount1Label : dataType.totalCountLabel;
		const barChartData2 = {
			barStyling: dataType.regionalandComponent.barStyling ? dataType.regionalandComponent.barStyling : barStyling2,
			headerText: getChartHeader(
				EngagementDataViewType.regionalAndComponent,
				regionalComponentEngagementData.totalCount,
				chartTitleHeader,
				appendedLabel
			),
			barMaxValue: regionalComponentEngagementData.totalCount,
			showBarTitleTip: true,
			barItems: barItems2,
			totalCount: regionalComponentEngagementData.totalCount,
			showChartSeries: regionalComponentEngagementData.totalCount === 0 ? false : true,
			onColumClick: (e) => onColumClick(e, 1)
		};

		setChartData2(barChartData2);
	};

	const switchToggle = (value) => {
		let selectedItem =
			config.toggleOptions &&
			config.toggleOptions.find((opt) => {
				return opt.id == value;
			});
		if (selectedItem) {
			let currentState = loadStorage(psessionKEy);
			setToggleOptionSwitcher(selectedItem);
			if (!currentState) {
				currentState = {};
			}
			const selectedToggleValue = type === 'finding' ? 'auditFindingFilterid' : 'auditStrategyFilterid';
			currentState[selectedToggleValue] = selectedItem;
			saveStorage(psessionKEy, currentState);
		}
	};

	const showPlaceHoldermessage = () => {
		let show = false;
		switch (props.viewType) {
			case AuditInsightViewType.groupRole:
				if (chartData1 && chartData2 && chartData1.totalCount <= 0 && chartData2.totalCount <= 0) {
					show = true;
				}

				break;
			case AuditInsightViewType.all:
				if (chartData1 && chartData1.totalCount <= 0) {
					show = true;
				}
				break;
		}
		return show;
	};

	const renderHeader = () => {
		const selectProps = {
			value: `${toggleOption?.id}`,
			onChange: switchToggle,
			options: config.toggleOptions,
			wrapperClass: 'card-header-select-size'
		};

		return (
			<>
				{config.toggleOptions && <Select {...selectProps} />}
				{props.exportDataTool && props.exportDataTool}
			</>
		);
	};

	const renderChart = (chartData) => {
		const isEmpty = chartData.totalCount <= 0;

		const {showCustomLegend = false, headerText = '', ...rest} = chartData;

		const legendProps = {
			items: chartData.barItems,
			horizontal: true,
			renderItem: ({label}) => (
				<OutputText body3 light className="grey">
					{label}
				</OutputText>
			)
		};

		const chartProps = {
			chartType: 'stackedBarChart',
			chartData: rest
		};

		return (
			<>
				{showCustomLegend && <Legend {...legendProps} />}
				{isEmpty && <OutputText body2>{Utility.createRichTextElement(labels.noAuditSightGroupData)}</OutputText>}
				{!isEmpty && (
					<div>
						<OutputText body2>{headerText}</OutputText>
						<StackedBarChart {...chartProps} />
					</div>
				)}
			</>
		);
	};

	const tileProps = {
		title: config.tileTitle,
		subTitle: config.subTitle ? config.subTitle : toggleOption.subTitle ? toggleOption.subTitle : toggleOption.subTitle,
		header: renderHeader(),
		isEmpty: showPlaceHoldermessage(),
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	return (
		<Tile {...tileProps}>
			{chartData1 && renderChart(chartData1)}
			{chartData2 && renderChart(chartData2)}
		</Tile>
	);
};

export default AuditInsightTileLayout;
